import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Tab,
  Tabs,
  CircularProgress,
  useTheme,
} from "@mui/material";
import PageContent from "../../../components/PageContent";
import PageHeader from "../../../components/PageHeader";
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Message as MessageIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import { useRecoilValue } from "recoil";
import organizationIdState from "../../../atoms/organizationIdAtom";
import useRenderCount from "../../../hooks/useRenderCount";
import { WebAnalytics, ANALYTICS_EVENTS } from "@mighty45/analytics-shared";


// Dashboard module components
import OverviewModule from "./dashboard/Overview/OverviewModule";
import CommunicationModule from "./dashboard/Communication/CommunicationModule";
import ActivityModule from "./dashboard/Activity/ActivityModule";
import BusinessModule from "./dashboard/Business/BusinessModule";
import ClientsModule from "./dashboard/Clients/ClientsModule";

const DashboardPage = () => {
  useRenderCount({ name: "DashboardPage" });

  const theme = useTheme();
  const organizationId = useRecoilValue(organizationIdState);
  const [searchParams, setSearchParams] = useSearchParams();
  const analytics = React.useMemo(() => new WebAnalytics({}), []);



  // Define tab mapping for URL navigation (memoized to prevent re-renders)
  const tabMapping = useMemo(
    () => ({
      overview: 0,
      communication: 1,
      activity: 2,
      clients: 3,
      business: 4,
    }),
    [],
  );

  // Reverse mapping for converting tab index to URL key
  const reverseTabMapping = useMemo(
    () =>
      Object.fromEntries(Object.entries(tabMapping).map(([k, v]) => [v, k])),
    [tabMapping],
  );

  // Track initial mount to prevent unnecessary URL update
  const isInitialMount = useRef(true);

  // Initialize selected tab from URL or localStorage
  const getInitialTab = () => {
    const urlTab = searchParams.get("tab");
    if (urlTab && Object.prototype.hasOwnProperty.call(tabMapping, urlTab)) {
      return tabMapping[urlTab];
    }

    const savedTab = localStorage.getItem("dashboard-selected-tab");
    if (savedTab && !isNaN(parseInt(savedTab))) {
      return parseInt(savedTab);
    }

    return 0; // Default to Overview
  };

  const [selectedTab, setSelectedTab] = useState(() => getInitialTab());

  // Single-direction URL sync - ONLY update URL when user clicks tabs
  useEffect(() => {
    // Skip initial mount to prevent unnecessary URL update
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const tabKey = reverseTabMapping[selectedTab];

    // Save to localStorage
    localStorage.setItem("dashboard-selected-tab", selectedTab.toString());

    // Update URL only if different from current
    const currentTab = searchParams.get("tab");
    const shouldUpdate =
      (tabKey === "overview" && currentTab !== null) ||
      (tabKey !== "overview" && currentTab !== tabKey);

    if (shouldUpdate) {
      const newSearchParams = new URLSearchParams(searchParams);
      if (tabKey === "overview") {
        newSearchParams.delete("tab");
      } else {
        newSearchParams.set("tab", tabKey);
      }

      setSearchParams(newSearchParams, { replace: true });
    }
  }, [selectedTab, reverseTabMapping]); // Removed searchParams dependency to break the loop

  // Handle URL changes ONLY on mount and browser navigation
  useEffect(() => {
    const urlTab = searchParams.get("tab");
    const expectedTab = urlTab ? tabMapping[urlTab] || 0 : 0;

    // Only update on initial mount or if tab is different
    if (isInitialMount.current || expectedTab !== selectedTab) {
      setSelectedTab(expectedTab);
    }
  }, [searchParams, tabMapping, selectedTab]);

  // Track dashboard page view ONLY on initial mount
  useEffect(() => {
    // Only track if we have a valid organization ID
    if (!organizationId) {
      return;
    }

    const tabKey = reverseTabMapping[selectedTab] || "overview";

    // Track dashboard page view on mount only
    analytics.trackScreenView("Dashboard", {
      tab: tabKey,
      organization_id: organizationId,
      entry_point: "navigation",
    });

    // Track feature usage on mount only
    analytics.trackCustomEvent(ANALYTICS_EVENTS.FEATURE_USED, {
      feature: "dashboard",
      module: tabKey,
      organization_id: organizationId,
    });

    // Track initial page load performance
    const loadStartTime = Date.now();
    const timeoutId = setTimeout(() => {
      const loadTime = Date.now() - loadStartTime;
      analytics.trackPerformance("dashboard_load_time", loadTime, {
        tab: tabKey,
        organization_id: organizationId,
      });
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [analytics, organizationId, reverseTabMapping, selectedTab]); // Run when these change

  // Track tab changes ONLY (skip initial mount)
  useEffect(() => {
    // Skip initial mount to avoid duplicate events
    if (isInitialMount.current) {
      return;
    }

    // Only track if we have a valid organization ID
    if (!organizationId) {
      return;
    }

    const tabKey = reverseTabMapping[selectedTab] || "overview";

    analytics.trackCustomEvent(ANALYTICS_EVENTS.TAB_SWITCHED, {
      screen: "Dashboard",
      tab: tabKey,
      organization_id: organizationId,
    });
  }, [selectedTab, reverseTabMapping, analytics, organizationId]); // Track tab changes only  // Memoize the dashboardTabs array to prevent re-renders
  const dashboardTabs = useMemo(
    () => [
      {
        label: "Overview",
        icon: <DashboardIcon />,
        component: OverviewModule,
        description: "Key metrics and summary",
        title: "Dashboard Overview",
        moduleDescription: "Key performance indicators and business summary",
      },
      {
        label: "Communication",
        icon: <MessageIcon />,
        component: CommunicationModule,
        description: "Message analytics and response times",
        title: "Communication Analytics",
        moduleDescription: "Message volume, response times, and topic analysis",
      },
      {
        label: "Activity",
        icon: <TrendingUpIcon />,
        component: ActivityModule,
        description: "Engagement and workout completion",
        title: "Activity & Engagement",
        moduleDescription:
          "Workout completion rates and client engagement tracking",
      },
      {
        label: "Clients",
        icon: <PeopleIcon />,
        component: ClientsModule,
        description: "Client roster and attention flags",
        title: "Client Analytics",
        moduleDescription:
          "Full client roster with performance, compliance, and needs-attention flags",
      },
      {
        label: "Business",
        icon: <BusinessIcon />,
        component: BusinessModule,
        description: "Growth, retention, and workload",
        title: "Business Intelligence",
        moduleDescription:
          "Client pipeline, coach workload, tenure segments, and operational gaps",
      },
    ],
    [],
  );

  const handleTabChange = useCallback(
    (event, newValue) => {
      const oldTabKey = reverseTabMapping[selectedTab];
      const newTabKey = reverseTabMapping[newValue];

      // Track tab change
      analytics.trackCustomEvent("user_action", {
        action: "dashboard_tab_clicked",
        element: "tab_button",
        from_tab: oldTabKey,
        to_tab: newTabKey,
        tab_index: newValue,
        organization_id: organizationId,
      });

      setSelectedTab(newValue);
    },
    [selectedTab, reverseTabMapping, analytics, organizationId],
  );

  const currentTab = useMemo(
    () => dashboardTabs[selectedTab],
    [dashboardTabs, selectedTab],
  );
  const CurrentTabComponent = useMemo(
    () => currentTab.component,
    [currentTab.component],
  );

  return (
    <PageContent
      header={
        <PageHeader
          title="Dashboard"
          subtitle="Business insights and analytics for your coaching practice"
        />
      }
    >
      {/* Navigation Tabs */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            background: theme.palette.background.paper,
            borderRadius: 1,
            boxShadow: theme.shadows[1],
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTab-root": {
                minHeight: 72,
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.95rem",
                color: theme.palette.text.secondary,
                "&.Mui-selected": {
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                },
              },
            }}
          >
            {dashboardTabs.map((tab, index) => (
              <Tab
                key={index}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {tab.icon}
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "inherit" }}
                      >
                        {tab.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ opacity: 0.7, display: "block" }}
                      >
                        {tab.description}
                      </Typography>
                    </Box>
                  </Box>
                }
                sx={{ alignItems: "flex-start", py: 1 }}
              />
            ))}
          </Tabs>
        </Box>
      </Box>

      {/* Tab Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        {!organizationId ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <CurrentTabComponent
              title={currentTab.title}
              description={currentTab.moduleDescription}
              organizationId={organizationId}
            />
          </Box>
        )}
      </Box>
    </PageContent>
  );
};

export default DashboardPage;
